import { FacilityDropdownInput } from "#src/batteries-included-components/Dropdowns/FacilityDropdownInput";
import useLocalization from "#src/hooks/useLocalization";
import { useMultiStepFormContext } from "#src/hooks/useMultiStepForm";
import {
  DropdownInput,
  Form,
  Panel,
  TextInput,
  useForm,
} from "@validereinc/common-components";
import {
  AssetType,
  AssetTypeType,
  DeviceSchemaEnumKeys,
  DeviceStatus,
  DeviceType,
  DeviceTypeType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect } from "react";
import styles from "./CreateDevicePage.module.css";
const cx = classNames.bind(styles);

const STEP_NUMBER = 1;

export const DeviceOverviewFormStep = ({
  shouldUseAssetBuilder,
  deviceTypes,
  isLoadingDeviceTypes,
}: {
  shouldUseAssetBuilder?: boolean;
  deviceTypes: DeviceTypeType[];
  isLoadingDeviceTypes?: boolean;
}) => {
  const { localize, isLoading: isMappingLoading } = useLocalization();
  const { currentStep, updateStep } = useMultiStepFormContext();
  const urlSearchParams = new URLSearchParams(document.location.search);
  const facilityId = urlSearchParams.get("facilityId");

  const form = useForm<
    {
      asset_type: AssetTypeType;
      template_name?: string;
    } & Pick<DeviceType, "name" | "type_id" | "status" | "facility_id">
  >({
    defaultValues: {
      asset_type: AssetType.DEVICE,
      [DeviceSchemaEnumKeys.name]: "",
      [DeviceSchemaEnumKeys.type_id]: "",
      [DeviceSchemaEnumKeys.status]: DeviceStatus.ACTIVE,
      [DeviceSchemaEnumKeys.facility_id]: facilityId ?? "",
      template_name: "",
    },
  });

  const formValues = form.watch();

  useEffect(() => {
    updateStep(STEP_NUMBER, {
      getValues: () => form.getValues(),
      getFormState: () => ({
        isSubmitting: false,
        isValid: !shouldUseAssetBuilder
          ? !!formValues[DeviceSchemaEnumKeys.name] &&
            !!formValues[DeviceSchemaEnumKeys.facility_id]
          : true,
      }),
    });
  }, [JSON.stringify(formValues)]);

  useEffect(() => {
    const emissionsMonitor = deviceTypes.find(
      ({ id }) => id === "emissions_monitor"
    );

    if (emissionsMonitor) {
      form.setValue(DeviceSchemaEnumKeys.type_id, emissionsMonitor.id);
    }
  }, [deviceTypes]);

  const isLoading = isLoadingDeviceTypes || isMappingLoading;

  if (currentStep !== STEP_NUMBER) return null;

  return (
    <Panel
      className={cx("panel")}
      loaded={!isLoading}
    >
      <Form {...form}>
        <h4 className={cx("section-title")}>Overview</h4>

        {shouldUseAssetBuilder ? null : (
          <p
            className={cx("section-description")}
          >{`Quick Create will create only the single ${localize(AssetType.DEVICE)} whose details are specified below.`}</p>
        )}
        <div className={cx("basic-info-container")}>
          <TextInput
            label="Name"
            name="name"
            placeholder="Enter Name..."
            isFluid
            isRequired
          />

          <DropdownInput
            name={DeviceSchemaEnumKeys.type_id}
            label={`${localize("Device")} Type`}
            valueKey="id"
            labelKey="name"
            options={deviceTypes}
            placeholder={`Enter ${localize("Device")} Type`}
            isSortedAlphabetically
            isRequired
            isFluid
          />

          <FacilityDropdownInput
            name="facility_id"
            placeholder={`Select ${localize(`${AssetType.FACILITY}`)}...`}
            isFluid
            isDisabled={Boolean(facilityId)}
            isMulti={false}
            isOptionalTextShown={false}
            isRequired
          />
        </div>
      </Form>
    </Panel>
  );
};
